import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { fmtCur, fmtNum, fmtDate } from "../lib/format";
import { CloudArrowUp, CheckCircle, FileXls, Warning, XCircle, Trash, Database, CalendarX, ArrowRight } from "@phosphor-icons/react";

export default function ImportExcel() {
  const [files, setFiles] = useState([]);
  const [currency, setCurrency] = useState("USD");
  const [openOnly, setOpenOnly] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [preview, setPreview] = useState(null);   // { results, file_count, totals }
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [committed, setCommitted] = useState(null); // { results, total_po_inserted, total_inv_inserted }
  const [importedCustomers, setImportedCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [deleting, setDeleting] = useState(null); // pcode|name being deleted
  const [confirmTarget, setConfirmTarget] = useState(null); // customer row pending confirm
  const [anomalies, setAnomalies] = useState(null); // { invoices, pos, count }
  const [loadingAnomalies, setLoadingAnomalies] = useState(false);
  const [applyingAnomalies, setApplyingAnomalies] = useState(false);
  const [selectedAnomalies, setSelectedAnomalies] = useState(new Set());

  const loadImportedCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const r = await api.get("/import/customers");
      setImportedCustomers(r.data.customers || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load imported customers");
    } finally { setLoadingCustomers(false); }
  };

  const anomalyId = (type, a) => `${type === "invoice" ? "invoice" : "po"}:${a.id}`;

  const [exporting, setExporting] = useState(false);
  const [transferPlan, setTransferPlan] = useState(null); // dry-run summary
  const [transferBusy, setTransferBusy] = useState(false); // "preview" | "commit"
  const [transferResult, setTransferResult] = useState(null); // written summary

  const previewTransfer = async () => {
    setTransferBusy("preview");
    setTransferResult(null);
    try {
      const r = await api.post("/replica/transfer", { confirm: false });
      setTransferPlan(r.data);
      toast.success(`Dry-run: ${r.data.po_count} POs / ${r.data.line_count} lines ready for New_PO / New_POi.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer preview failed");
    } finally { setTransferBusy(false); }
  };

  const commitTransfer = async () => {
    if (!window.confirm("Transfer to MS SQL replica tables New_PO / New_POi?\n\nThis FULL-REPLACES both tables (wipes and re-writes the current open POs). The live po/poi tables are NOT touched.")) return;
    setTransferBusy("commit");
    try {
      const r = await api.post("/replica/transfer", { confirm: true });
      setTransferResult(r.data);
      toast.success(`Transferred ${r.data.po_written} POs & ${r.data.line_written} lines to New_PO / New_POi.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed", { duration: 8000 });
    } finally { setTransferBusy(false); }
  };

  const exportAll = async () => {
    setExporting(true);
    try {
      const r = await api.get("/export/full-xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = `mongo_export_${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  const loadAnomalies = async () => {
    setLoadingAnomalies(true);
    try {
      const r = await api.get("/import/date-anomalies");
      setAnomalies(r.data);
      // pre-select invoice anomalies (most reliable signal); leave POs unchecked
      const pre = new Set((r.data.invoices || []).map((a) => anomalyId("invoice", a)));
      setSelectedAnomalies(pre);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load date validation");
    } finally { setLoadingAnomalies(false); }
  };

  useEffect(() => { loadImportedCustomers(); loadAnomalies(); }, []);

  const toggleAnomaly = (id) => {
    setSelectedAnomalies((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const applyAnomalies = async (all) => {
    const ids = all ? null : Array.from(selectedAnomalies);
    if (!all && ids.length === 0) return toast.error("Select at least one row to fix");
    setApplyingAnomalies(true);
    try {
      const r = await api.post("/import/date-anomalies/apply", { ids });
      toast.success(`Corrected ${r.data.invoices_fixed} invoices & ${r.data.pos_fixed} POs.`);
      await loadAnomalies();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setApplyingAnomalies(false); }
  };

  const deleteCustomer = async (c) => {
    const key = c.pcode || c.name;
    setDeleting(key);
    try {
      const params = c.pcode ? { pcode: c.pcode } : { name: c.name };
      const r = await api.delete("/import/customer", { params });
      toast.success(`Deleted ${r.data.po_deleted} POs & ${r.data.inv_deleted} invoices for ${c.name || c.pcode}.`);
      setConfirmTarget(null);
      await loadImportedCustomers();
      await loadAnomalies();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally { setDeleting(null); }
  };

  const buildFormData = (includeFresh = false) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("plant", "P9");
    fd.append("default_currency", currency);
    fd.append("open_only", openOnly ? "true" : "false");
    if (includeFresh) fd.append("fresh", fresh ? "true" : "false");
    return fd;
  };

  const onPickFiles = (e) => {
    setFiles(Array.from(e.target.files || []));
    setPreview(null);
    setCommitted(null);
  };

  const runPreview = async () => {
    if (!files.length) return toast.error("Pick one or more Excel files first");
    setLoading(true);
    setCommitted(null);
    setPreview(null);
    try {
      const start = await api.post("/import/excel-multi-preview", buildFormData(), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = start.data.job_id;
      if (!jobId) throw new Error("No job id returned");
      // Poll the background preview job until it finishes (survives proxy timeouts on
      // large batches — previewing 16+ workbooks used to exceed the ~100s edge limit).
      const poll = async () => (await api.get(`/import/preview-job/${jobId}`)).data;
      let job = await poll();
      while (job.status === "running") {
        await new Promise((res) => setTimeout(res, 2000));
        job = await poll();
      }
      if (job.status === "error") {
        const msg = job.error || "Preview failed";
        toast.error(msg, { duration: 8000 });
        setPreview({ error: msg });
        return;
      }
      setPreview(job);
      const okCount = (job.results || []).filter((x) => x.ok).length;
      toast.success(`Preview ready · ${okCount}/${job.file_count} files parsed · ${job.totals.po_count} POs, ${job.totals.open_po_count} open.`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Preview failed";
      toast.error(msg, { duration: 8000 });
      setPreview({ error: msg });
    } finally { setLoading(false); }
  };

  const commitImport = async () => {
    if (!preview || !files.length) return;
    setCommitting(true);
    try {
      const start = await api.post("/import/excel-multi", buildFormData(true), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const jobId = start.data.job_id;
      if (!jobId) throw new Error("No job id returned");
      toast.message(`Importing ${start.data.file_count} file(s) in the background…`);
      // Poll the background job until it finishes (survives proxy timeouts on big files).
      const poll = async () => {
        const s = await api.get(`/import/job/${jobId}`);
        return s.data;
      };
      let job = await poll();
      while (job.status === "running") {
        await new Promise((res) => setTimeout(res, 2500));
        job = await poll();
      }
      if (job.status === "error") {
        toast.error(`Import failed: ${job.error || "unknown error"}`, { duration: 8000 });
        return;
      }
      setCommitted(job);
      const clr = job.fresh_cleared
        ? ` (cleared ${job.fresh_cleared.pos_deleted} prior POs)` : "";
      toast.success(`Imported ${job.total_po_inserted} POs & ${job.total_inv_inserted} invoices from ${job.file_count} files${clr}.`);
      loadImportedCustomers();
      loadAnomalies();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Import failed");
    } finally { setCommitting(false); }
  };

  const t = preview && !preview.error ? preview.totals : null;
  const curCode = currency || "USD";
  const okFiles = preview && !preview.error ? (preview.results || []).filter((x) => x.ok) : [];
  const failedFiles = preview && !preview.error ? (preview.results || []).filter((x) => !x.ok) : [];

  return (
    <Layout breadcrumb={["Home", "Adhoc", "Import Order Excel"]}>
      <div className="jde-page">
        {/* === STEP 1: UPLOAD === */}
        <div className="jde-panel" data-testid="import-upload-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title"><FileXls size={13} weight="bold" /> Step 1 — Upload workbooks</div>
            <div className="muted" style={{ fontSize: 11 }}>Customer is auto-detected per file (standardized Open Order Report).</div>
          </div>
          <div className="jde-panel-body" style={{ display: "grid", gap: 12 }}>
            <div style={{ maxWidth: 175 }}>
              <label className="muted" style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>DEFAULT CURRENCY</label>
              <select className="jde-input" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="input-currency">
                <option value="USD">USD ($)</option>
                <option value="GBP">GBP (£)</option>
                <option value="INR">INR (₹)</option>
              </select>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", maxWidth: 340 }} data-testid="toggle-open-only">
                <input type="checkbox" checked={openOnly} onChange={(e) => { setOpenOnly(e.target.checked); setPreview(null); setCommitted(null); }}
                       data-testid="open-only-checkbox" style={{ marginTop: 3 }} />
                <span style={{ fontSize: 12 }}>
                  <strong>Open POs only</strong>
                  <div className="muted" style={{ fontSize: 11 }}>Off by default — <strong>all POs are imported</strong> (including fully-shipped/closed). Tick this only if you want to skip closed POs and keep just those with an open line.</div>
                </span>
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer", maxWidth: 340 }} data-testid="toggle-fresh">
                <input type="checkbox" checked={fresh} onChange={(e) => setFresh(e.target.checked)}
                       data-testid="fresh-checkbox" style={{ marginTop: 3 }} />
                <span style={{ fontSize: 12 }}>
                  <strong>Fresh import</strong>
                  <div className="muted" style={{ fontSize: 11 }}>Clear ALL previously imported Excel POs/invoices before importing (SAP-synced data is untouched).</div>
                </span>
              </label>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12,
                          border: "2px dashed var(--jde-border)", borderRadius: 4, background: "#FAFBFC" }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                multiple
                onChange={onPickFiles}
                data-testid="file-input"
              />
              <div className="muted" style={{ fontSize: 11 }}>
                {files.length ? <strong>{files.length} file{files.length > 1 ? "s" : ""} selected</strong> : "Drop or pick one or more order workbooks"}
              </div>
              <div style={{ flex: 1 }} />
              <button className="jde-btn" onClick={runPreview} disabled={!files.length || loading} data-testid="btn-preview">
                <CloudArrowUp size={13} weight="bold" /> {loading ? "Parsing…" : `Preview ${files.length || ""}`.trim()}
              </button>
            </div>

            {/* selected file chips */}
            {files.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="selected-files">
                {files.map((f, i) => (
                  <span key={i} className="text-mono" data-testid={`selected-file-${i}`} style={{
                    fontSize: 11, background: "#EEF2FF", border: "1px solid #C7D2FE",
                    color: "#3730A3", padding: "3px 8px", borderRadius: 12,
                  }}>
                    <FileXls size={11} weight="bold" style={{ marginRight: 4, verticalAlign: "-1px" }} />
                    {f.name} <span className="muted">· {f.size >= 1024 * 1024 ? `${(f.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* === STEP 2: REVIEW === */}
        {preview?.error && (
          <div data-testid="preview-error" style={{
            marginTop: 14, padding: 12, background: "#FEE2E2", border: "1px solid #FCA5A5",
            borderRadius: 4, color: "#991B1B", fontSize: 12,
          }}>
            <strong>Preview failed:</strong> {preview.error}
          </div>
        )}

        {preview && !preview.error && (
          <>
            {/* grand totals */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 14 }}>
              <Tile label="Files parsed" value={`${okFiles.length}/${preview.file_count}`} sub={failedFiles.length ? `${failedFiles.length} failed` : "all ok"} color={failedFiles.length ? "#B91C1C" : "#0F766E"} />
              <Tile label="POs found" value={fmtNum(t.po_count)} sub={`${t.open_po_count} open`} />
              <Tile label="Open qty" value={fmtNum(t.qty_open_total)} sub="across all files" color="#B91C1C" />
              <Tile label="Open value" value={fmtCur(t.open_value_total, curCode)} sub="qty × PO price" color="#0F766E" />
            </div>

            {/* date anomaly warning (pre-commit) */}
            {(preview.total_date_anomalies || 0) > 0 && (
              <div className="jde-panel" style={{ marginTop: 14, border: "1px solid #FCD34D" }} data-testid="preview-anomalies-panel">
                <div className="jde-panel-header" style={{ background: "#FFFBEB" }}>
                  <div className="jde-panel-title" style={{ color: "#92400E" }}>
                    <CalendarX size={13} weight="bold" /> {preview.total_date_anomalies} possible date typo{preview.total_date_anomalies > 1 ? "s" : ""} detected
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>Same invoice/PO number with a later-year date than a sibling. Fix the source file, or import then correct under "Date validation" below.</div>
                </div>
                <div className="jde-panel-body" style={{ padding: 0 }}>
                  <table className="jde-grid" data-testid="preview-anomalies-table">
                    <thead><tr><th>File</th><th>Type</th><th>Number</th><th>Part</th><th>In file</th><th></th><th>Looks like</th></tr></thead>
                    <tbody>
                      {(preview.results || []).flatMap((r) => (r.date_anomalies || []).map((a) => ({ ...a, file: r.file })))
                        .map((a, i) => (
                          <tr key={i} data-testid={`preview-anomaly-${i}`}>
                            <td className="text-mono" style={{ fontSize: 11 }}>{a.file}</td>
                            <td><span className="jde-badge">{a.type}</span></td>
                            <td className="text-mono"><strong>{a.number}</strong></td>
                            <td className="text-mono" style={{ fontSize: 11 }}>{a.part_no || "—"}</td>
                            <td className="text-mono" style={{ color: "#B91C1C", fontWeight: 600 }}>{fmtDate(a.current_date)}</td>
                            <td><ArrowRight size={12} weight="bold" style={{ color: "#92400E" }} /></td>
                            <td className="text-mono" style={{ color: "#065F46", fontWeight: 600 }}>{fmtDate(a.suggested_date)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* per-file table */}
            <div className="jde-panel" style={{ marginTop: 14 }}>
              <div className="jde-panel-header">
                <div className="jde-panel-title">Step 2 — Review per file ({preview.file_count})</div>
              </div>
              <div className="jde-panel-body" style={{ padding: 0 }}>
                <table className="jde-grid" data-testid="preview-files-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}></th>
                      <th>File</th>
                      <th>Detected customer</th>
                      <th>Format</th>
                      <th>Cur</th>
                      <th style={{ textAlign: "right" }}>POs</th>
                      <th style={{ textAlign: "right" }}>Open POs</th>
                      <th style={{ textAlign: "right" }}>Qty Open</th>
                      <th style={{ textAlign: "right" }}>Open Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.results || []).map((r, i) => (
                      <tr key={i} data-testid={`file-row-${i}`} style={{ background: r.ok ? undefined : "#FEF2F2" }}>
                        <td>{r.ok ? <CheckCircle size={13} weight="fill" style={{ color: "#10B981" }} /> : <XCircle size={13} weight="fill" style={{ color: "#EF4444" }} />}</td>
                        <td className="text-mono"><strong>{r.file}</strong></td>
                        {r.ok ? (
                          <>
                            <td data-testid={`file-customer-${i}`}>{r.customer_name || "—"}</td>
                            <td><span className="jde-badge">{r.format === "open_order_report" ? "standard" : "legacy"}</span></td>
                            <td className="text-mono">{r.currency || "—"}</td>
                            <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(r.po_count)}</td>
                            <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(r.open_po_count)}</td>
                            <td className="text-mono" style={{ textAlign: "right", fontWeight: 700, color: r.qty_open_total > 0 ? "#B91C1C" : "#10B981" }}>{fmtNum(r.qty_open_total)}</td>
                            <td className="text-mono" style={{ textAlign: "right", fontWeight: 600 }}>{fmtCur(r.open_value_total, r.currency || curCode)}</td>
                          </>
                        ) : (
                          <td colSpan={7} style={{ color: "#991B1B", fontSize: 11 }} data-testid={`file-error-${i}`}>
                            <Warning size={12} weight="fill" style={{ marginRight: 4, verticalAlign: "-1px" }} /> {r.error}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* === STEP 3: APPROVE === */}
            <div style={{ marginTop: 14 }}>
              {committed ? (
                <div data-testid="commit-success">
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 8, color: "#065F46",
                    background: "#D1FAE5", border: "1px solid #6EE7B7", padding: "8px 12px", borderRadius: 4,
                    fontWeight: 600, fontSize: 12,
                  }}>
                    <CheckCircle size={14} weight="fill" />
                    Imported {committed.total_po_inserted} POs &amp; {committed.total_inv_inserted} invoices from {committed.file_count} files to MongoDB.
                  </div>
                  <table className="jde-grid" style={{ marginTop: 10 }} data-testid="commit-results-table">
                    <thead><tr><th>File</th><th>Customer</th><th style={{ textAlign: "right" }}>POs</th><th style={{ textAlign: "right" }}>Invoices</th><th>Status</th></tr></thead>
                    <tbody>
                      {(committed.results || []).map((r, i) => (
                        <tr key={i} data-testid={`commit-row-${i}`} style={{ background: r.ok ? undefined : "#FEF2F2" }}>
                          <td className="text-mono"><strong>{r.file}</strong></td>
                          <td>{r.ok ? (r.customer_name || "—") : "—"}</td>
                          <td className="text-mono" style={{ textAlign: "right" }}>{r.ok ? fmtNum(r.po_inserted) : "—"}</td>
                          <td className="text-mono" style={{ textAlign: "right" }}>{r.ok ? fmtNum(r.inv_inserted) : "—"}</td>
                          <td style={{ fontSize: 11, color: r.ok ? "#065F46" : "#991B1B" }}>{r.ok ? "imported" : r.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, fontSize: 11 }} className="muted">
                    <Warning size={12} weight="fill" style={{ color: "#F59E0B", marginRight: 4 }} />
                    Re-uploading the same filename replaces previous import rows from that file. Excel-imported docs are kept safe from SAP sync.
                  </div>
                  <button className="jde-btn jde-btn-primary" onClick={commitImport} disabled={committing || okFiles.length === 0} data-testid="btn-commit">
                    <CheckCircle size={13} weight="bold" /> {committing ? "Writing…" : `Approve & Import ${okFiles.length} file${okFiles.length > 1 ? "s" : ""} to MongoDB`}
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* === EXPORT DATA FOR REVIEW === */}
        <div className="jde-panel" style={{ marginTop: 14 }} data-testid="export-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title"><Database size={13} weight="bold" /> Export all data (Excel)</div>
            <div className="muted" style={{ fontSize: 11 }}>Downloads every PO, PO line, and invoice/dispatch from the database into one .xlsx (POs / PO_Lines / Invoices sheets). Read-only — nothing is changed.</div>
          </div>
          <div className="jde-panel-body" style={{ padding: 12 }}>
            <button className="jde-btn jde-btn-primary" disabled={exporting} onClick={exportAll} data-testid="export-all-btn">
              <CloudArrowUp size={13} weight="bold" style={{ transform: "rotate(180deg)" }} /> {exporting ? "Preparing…" : "Download full export (.xlsx)"}
            </button>
          </div>
        </div>

        {/* === TRANSFER TO MS SQL REPLICA (New_PO / New_POi) === */}
        <div className="jde-panel" style={{ marginTop: 14, border: "1px solid #6366F1" }} data-testid="replica-transfer-panel">
          <div className="jde-panel-header" style={{ background: "#EEF2FF" }}>
            <div className="jde-panel-title" style={{ color: "#3730A3" }}><Database size={13} weight="bold" /> Transfer to MS SQL replica (New_PO / New_POi)</div>
            <div className="muted" style={{ fontSize: 11 }}>Writes the imported open POs into the dedicated replica tables <code>dbo.New_PO</code> (header) + <code>dbo.New_POi</code> (lines). Customer Pcode is canonical (Walmart→WMRT, Boffin→BIPL, Lowes→LGS, GeoTek→GTEK…). Full-replace &amp; idempotent. The live <code>po/poi</code> tables are never touched.</div>
          </div>
          <div className="jde-panel-body" style={{ padding: 12, display: "grid", gap: 12 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="jde-btn" disabled={!!transferBusy} onClick={previewTransfer} data-testid="transfer-preview-btn">
                {transferBusy === "preview" ? "Checking…" : "1 · Preview transfer (dry-run)"}
              </button>
              <button className="jde-btn jde-btn-primary" disabled={!transferPlan || !!transferBusy} onClick={commitTransfer} data-testid="transfer-commit-btn">
                <Database size={13} weight="bold" /> {transferBusy === "commit" ? "Writing…" : "2 · Transfer to MS SQL"}
              </button>
            </div>

            {transferPlan && (
              <div data-testid="transfer-plan" style={{ background: "#F8FAFF", border: "1px solid #C7D2FE", borderRadius: 4, padding: 12 }}>
                <div style={{ fontSize: 13, marginBottom: 8 }}>
                  <strong>{transferPlan.po_count}</strong> PO headers · <strong>{transferPlan.line_count}</strong> lines ready
                  <span className="muted"> · {transferPlan.mode}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Object.entries(transferPlan.by_customer || {}).map(([k, v]) => (
                    <span key={k} className="text-mono" style={{ fontSize: 11, background: "#EEF2FF", border: "1px solid #C7D2FE", color: "#3730A3", padding: "3px 8px", borderRadius: 12 }}>
                      {k}: <strong>{v}</strong>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {transferResult && (
              <div data-testid="transfer-result" style={{ background: "#ECFDF5", border: "1px solid #6EE7B7", borderRadius: 4, padding: 12, fontSize: 13 }}>
                <CheckCircle size={14} weight="bold" style={{ verticalAlign: "-2px", color: "#059669" }} />{" "}
                <strong>Done — {transferResult.mode}.</strong> Wrote <strong>{transferResult.po_written}</strong> rows to <code>New_PO</code> and <strong>{transferResult.line_written}</strong> rows to <code>New_POi</code>.
              </div>
            )}
          </div>
        </div>

        {/* === DATE VALIDATION (existing imported data) === */}
        <div className="jde-panel" style={{ marginTop: 14 }} data-testid="date-validation-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title"><CalendarX size={13} weight="bold" /> Date validation — fix year typos in imported data</div>
            <div className="muted" style={{ fontSize: 11 }}>
              Same invoice/PO number with a later-year date than a sibling → suggests the earliest date. Original value is kept as an audit trail.
            </div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            {loadingAnomalies ? (
              <div className="muted" style={{ padding: 14, fontSize: 12 }}>Scanning…</div>
            ) : !anomalies || anomalies.count === 0 ? (
              <div className="muted" style={{ padding: 14, fontSize: 12 }} data-testid="no-anomalies">No date conflicts found in imported data.</div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: "1px solid var(--jde-border-subtle)" }}>
                  <div style={{ flex: 1, fontSize: 12 }}>
                    <strong>{anomalies.count}</strong> conflict{anomalies.count > 1 ? "s" : ""} ·{" "}
                    <span className="muted">{anomalies.invoices.length} invoice / {anomalies.pos.length} PO. Invoice conflicts are pre-selected.</span>
                  </div>
                  <button className="jde-btn" disabled={applyingAnomalies} onClick={() => applyAnomalies(false)} data-testid="apply-selected-anomalies">
                    {applyingAnomalies ? "Applying…" : `Apply selected (${selectedAnomalies.size})`}
                  </button>
                  <button className="jde-btn jde-btn-primary" disabled={applyingAnomalies} onClick={() => applyAnomalies(true)} data-testid="apply-all-anomalies">
                    <CheckCircle size={13} weight="bold" /> Apply all
                  </button>
                </div>
                <table className="jde-grid" data-testid="anomalies-table">
                  <thead>
                    <tr>
                      <th style={{ width: 28 }}></th>
                      <th>Type</th>
                      <th>Number</th>
                      <th>Part</th>
                      <th>Customer</th>
                      <th>Current</th>
                      <th></th>
                      <th>Suggested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...anomalies.invoices.map((a) => ({ ...a, _type: "invoice" })),
                      ...anomalies.pos.map((a) => ({ ...a, _type: "po" }))].map((a, i) => {
                      const id = anomalyId(a._type, a);
                      const checked = selectedAnomalies.has(id);
                      return (
                        <tr key={id} data-testid={`anomaly-row-${i}`}>
                          <td>
                            <input type="checkbox" checked={checked} onChange={() => toggleAnomaly(id)}
                                   data-testid={`anomaly-check-${i}`} />
                          </td>
                          <td><span className="jde-badge">{a._type}</span></td>
                          <td className="text-mono"><strong>{a._type === "invoice" ? a.ms_inv_no : a.ms_custpo}</strong></td>
                          <td className="text-mono" style={{ fontSize: 11 }}>{a.part_no || "—"}</td>
                          <td style={{ fontSize: 11 }}>{a.customer_name || "—"}</td>
                          <td className="text-mono" style={{ color: "#B91C1C", fontWeight: 600 }}>{fmtDate(a.current_date)}</td>
                          <td><ArrowRight size={12} weight="bold" style={{ color: "#92400E" }} /></td>
                          <td className="text-mono" style={{ color: "#065F46", fontWeight: 600 }}>{fmtDate(a.suggested_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>

        {/* === MANAGE IMPORTED DATA === */}
        <div className="jde-panel" style={{ marginTop: 14 }} data-testid="manage-imports-panel">
          <div className="jde-panel-header">
            <div className="jde-panel-title"><Database size={13} weight="bold" /> Manage imported data — delete by customer</div>
            <div className="muted" style={{ fontSize: 11 }}>Removes all Excel-imported POs &amp; invoices for a customer. SAP-synced data is never touched.</div>
          </div>
          <div className="jde-panel-body" style={{ padding: 0 }}>
            {loadingCustomers ? (
              <div className="muted" style={{ padding: 14, fontSize: 12 }}>Loading…</div>
            ) : importedCustomers.length === 0 ? (
              <div className="muted" style={{ padding: 14, fontSize: 12 }} data-testid="no-imported-customers">No Excel-imported customers yet.</div>
            ) : (
              <table className="jde-grid" data-testid="imported-customers-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Code</th>
                    <th style={{ textAlign: "right" }}>POs</th>
                    <th style={{ textAlign: "right" }}>Invoices</th>
                    <th>Source files</th>
                    <th style={{ width: 120, textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {importedCustomers.map((c, i) => {
                    const key = c.pcode || c.name;
                    const isConfirming = confirmTarget && (confirmTarget.pcode || confirmTarget.name) === key;
                    return (
                      <tr key={key} data-testid={`imported-customer-row-${i}`}>
                        <td><strong>{c.name || "—"}</strong></td>
                        <td className="text-mono">{c.pcode || "—"}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(c.po_count)}</td>
                        <td className="text-mono" style={{ textAlign: "right" }}>{fmtNum(c.inv_count)}</td>
                        <td className="text-mono muted" style={{ fontSize: 11 }}>{(c.source_files || []).join(", ") || "—"}</td>
                        <td style={{ textAlign: "right" }}>
                          {isConfirming ? (
                            <div style={{ display: "inline-flex", gap: 6 }}>
                              <button
                                className="jde-btn jde-btn-danger"
                                style={{ padding: "3px 8px", fontSize: 11, background: "#DC2626", color: "white", border: "1px solid #B91C1C" }}
                                disabled={deleting === key}
                                onClick={() => deleteCustomer(c)}
                                data-testid={`confirm-delete-customer-${i}`}
                              >
                                {deleting === key ? "Deleting…" : "Confirm"}
                              </button>
                              <button
                                className="jde-btn"
                                style={{ padding: "3px 8px", fontSize: 11 }}
                                disabled={deleting === key}
                                onClick={() => setConfirmTarget(null)}
                                data-testid={`cancel-delete-customer-${i}`}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              className="jde-btn"
                              style={{ padding: "3px 8px", fontSize: 11, color: "#B91C1C", borderColor: "#FCA5A5" }}
                              onClick={() => setConfirmTarget(c)}
                              data-testid={`delete-customer-${i}`}
                            >
                              <Trash size={12} weight="bold" /> Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Tile({ label, value, sub, color }) {
  return (
    <div style={{ background: "white", border: "1px solid var(--jde-border)", padding: "10px 12px", borderRadius: 4 }}>
      <div className="muted" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--jde-font-mono)", color: color || "#0F172A", marginTop: 4 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
